import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useConsoleStore } from '../store';

type StoreHealth = {
  id: string;
  externalDomain: string;
  platform: string;
  isActive: boolean;
  planTier: string;
  subscriptionStatus: string;
  billingProvider: string;
  trialEndsAt: string | null;
  gracePeriodEndsAt: string | null;
  planEffectiveUntil: string | null;
  aiInteractionsThisMonth: number;
  status: string;
};

type StoreRow = {
  id: string;
  externalDomain: string;
  platform: string;
  isActive: boolean;
  planTier: string;
  subscriptionStatus: string;
  billingProvider: string;
  createdAt: string;
};

type StoreOwner = { userId: string; email: string | null; role: string; linkedAt: string };

type UserRow = {
  id?: string;
  email: string;
  role: 'user' | 'support' | 'staff' | 'superadmin';
  createdAt?: string;
};

type LaunchFeedbackRow = {
  id: string;
  storeId: string | null;
  siteContext: 'marketing_site' | 'demo_site';
  trigger: 'widget_close' | 'exit_intent';
  storeDomain: string;
  page: string | null;
  sessionId: string | null;
  hadWidgetOpen: boolean;
  hadChatInteraction: boolean;
  messageCount: number | null;
  feedbackType: string;
  severity: string | null;
  goal: string | null;
  details: string;
  createdAt: string;
};

type LaunchFeedbackSummaryRow = {
  siteContext: 'marketing_site' | 'demo_site';
  trigger: 'widget_close' | 'exit_intent';
  count: number;
};

type ActionState = {
  loading: boolean;
  error: string | null;
  success: string | null;
};

const EMPTY_ACTION: ActionState = { loading: false, error: null, success: null };
const PLAN_OPTIONS = ['free', 'growth', 'agent', 'rep', 'vea'] as const;
const ADMIN_AREAS = [
  { id: 'fleet', label: 'Fleet', description: 'Totals, recent stores, plan mix, and grace risk.' },
  { id: 'store-desk', label: 'Store Desk', description: 'Health, owners, plan, activity, and interventions.' },
  { id: 'revenue', label: 'Revenue', description: 'Plans, promo codes, and billing exceptions.' },
  { id: 'system', label: 'System', description: 'Users, roles, action log, and internal tools.' },
] as const;
type AdminArea = (typeof ADMIN_AREAS)[number]['id'];

function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function errMessage(err: unknown): string {
  const e = err as any;
  return e?.message || e?.error || 'Request failed';
}

function errStatus(err: unknown): number | undefined {
  const e = err as any;
  return e?.status || e?.response?.status;
}

function roleFlags(role?: string) {
  const r = (role || '').toLowerCase();
  return {
    canSupport: r === 'support' || r === 'staff' || r === 'superadmin',
    canStaff: r === 'staff' || r === 'superadmin',
    canSuperadmin: r === 'superadmin',
  };
}

function platformBadge(platform?: string) {
  const p = (platform || '').toLowerCase();
  if (p === 'shopify') {
    return <span className="theme-badge theme-badge-shopify">Shopify</span>;
  }
  if (p === 'wordpress') {
    return <span className="theme-badge theme-badge-wordpress">WordPress</span>;
  }
  return <span className="theme-badge theme-badge-neutral">{platform || 'Unknown'}</span>;
}

function planBadge(plan?: string) {
  const p = (plan || '').toLowerCase();
  const map: Record<string, string> = {
    free: 'theme-badge-plan-free',
    growth: 'theme-badge-plan-growth',
    agent: 'theme-badge-plan-agent',
    rep: 'theme-badge-plan-rep',
    vea: 'theme-badge-plan-vea',
  };
  const cls = map[p] || 'theme-badge-neutral';
  return <span className={`theme-badge ${cls}`}>{plan || 'unknown'}</span>;
}

function statusBadge(status?: string) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'active'
      ? 'theme-badge-status-active'
      : s === 'past_due'
        ? 'theme-badge-status-past-due'
        : s === 'cancelled'
          ? 'theme-badge-status-cancelled'
          : 'theme-badge-neutral';
  return <span className={`theme-badge ${cls}`}>{status || 'unknown'}</span>;
}

function MetricCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
      <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-zinc-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

function SectionCard({ title, description, children, tone = 'default' }: {
  title: string;
  description?: string;
  children: ReactNode;
  tone?: 'default' | 'danger';
}) {
  return (
    <section className={tone === 'danger' ? 'rounded-2xl border border-red-200 bg-red-50 p-5' : 'theme-section-card'}>
      <div className="mb-4">
        <h3 className={tone === 'danger' ? 'text-lg font-semibold text-red-700' : 'text-lg font-semibold text-zinc-900'}>{title}</h3>
        {description && <p className={tone === 'danger' ? 'mt-1 text-sm text-red-700/80' : 'mt-1 text-sm text-zinc-500'}>{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function InternalAdmin() {
  const { user } = useConsoleStore();
  const { canSupport, canStaff, canSuperadmin } = roleFlags(user?.role);
  const [searchParams] = useSearchParams();
  const [adminArea, setAdminArea] = useState<AdminArea>('fleet');

  useEffect(() => {
    const domain = searchParams.get('domain');
    if (domain) {
      setAdminArea('store-desk');
      setLookupDomain(domain);
      lookupStore(domain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lookup
  const [lookupDomain, setLookupDomain] = useState('');
  const [resolvedStore, setResolvedStore] = useState<StoreHealth | null>(null);
  const [resolvedOwners, setResolvedOwners] = useState<StoreOwner[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Store actions
  const [activeReason, setActiveReason] = useState('');
  const [planTier, setPlanTier] = useState<(typeof PLAN_OPTIONS)[number]>('free');
  const [rotateReason, setRotateReason] = useState('');
  const [previewReason, setPreviewReason] = useState('Staff preview validation');
  const [trialDays, setTrialDays] = useState(7);
  const [trialReason, setTrialReason] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeReason, setScrapeReason] = useState('');
  const [rotatedKey, setRotatedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Add store
  const [addDomain, setAddDomain] = useState('');
  const [addPlatform, setAddPlatform] = useState<'shopify' | 'wordpress' | 'standard'>('standard');
  const [addEmail, setAddEmail] = useState('');
  const [addPlan, setAddPlan] = useState<(typeof PLAN_OPTIONS)[number]>('free');
  const [addState, setAddState] = useState<ActionState>(EMPTY_ACTION);

  // Delete store
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteState, setDeleteState] = useState<ActionState>(EMPTY_ACTION);

  // Action states
  const [setActiveState, setSetActiveState] = useState<ActionState>(EMPTY_ACTION);
  const [setPlanState, setSetPlanState] = useState<ActionState>(EMPTY_ACTION);
  const [rotateState, setRotateState] = useState<ActionState>(EMPTY_ACTION);
  const [previewState, setPreviewState] = useState<ActionState>(EMPTY_ACTION);
  const [extendTrialState, setExtendTrialState] = useState<ActionState>(EMPTY_ACTION);
  const [triggerScrapeState, setTriggerScrapeState] = useState<ActionState>(EMPTY_ACTION);
  const [userActionState, setUserActionState] = useState<Record<string, ActionState>>({});
  const [pendingRole, setPendingRole] = useState<Record<string, UserRow['role']>>({});
  const [feedbackSiteContext, setFeedbackSiteContext] = useState<'all' | 'marketing_site' | 'demo_site'>('all');
  const [feedbackSearch, setFeedbackSearch] = useState('');

  const storesQuery = useQuery({
    queryKey: ['internal-stores'],
    queryFn: async () => {
      const data = await apiFetch<any>('/api/internal/stores');
      if (Array.isArray(data)) return data as StoreRow[];
      if (Array.isArray(data?.stores)) return data.stores as StoreRow[];
      return [] as StoreRow[];
    },
  });

  const usersQuery = useQuery({
    queryKey: ['internal-users'],
    queryFn: async () => {
      const data = await apiFetch<any>('/api/internal/users');
      if (Array.isArray(data)) return data as UserRow[];
      if (Array.isArray(data?.users)) return data.users as UserRow[];
      return [] as UserRow[];
    },
  });

  const launchFeedbackQuery = useQuery({
    queryKey: ['internal-launch-feedback', feedbackSiteContext, feedbackSearch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('siteContext', feedbackSiteContext);
      if (feedbackSearch.trim()) params.set('q', feedbackSearch.trim());
      params.set('limit', '100');
      return await apiFetch<{ rows: LaunchFeedbackRow[]; total: number; summary: LaunchFeedbackSummaryRow[] }>(`/api/internal/launch-feedback?${params.toString()}`);
    },
    enabled: adminArea === 'system' && canSupport,
  });

  const filteredStores = useMemo(() => {
    const list = storesQuery.data || [];
    const needle = search.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((s) => s.externalDomain.toLowerCase().includes(needle));
  }, [storesQuery.data, search]);

  // Metrics derived from store list
  const metrics = useMemo(() => {
    const list = storesQuery.data || [];
    const total = list.length;
    const active = list.filter((s) => s.isActive).length;
    const shopify = list.filter((s) => s.platform === 'shopify').length;
    const wordpress = list.filter((s) => s.platform === 'wordpress').length;
    const standard = list.filter((s) => !['shopify', 'wordpress'].includes((s.platform || '').toLowerCase())).length;
    const byPlan = PLAN_OPTIONS.reduce<Record<string, number>>((acc, p) => {
      acc[p] = list.filter((s) => s.planTier === p).length;
      return acc;
    }, {});
    const paid = list.filter((s) => s.planTier !== 'free').length;
    return { total, active, inactive: total - active, shopify, wordpress, standard, byPlan, paid };
  }, [storesQuery.data]);

  const lookupStore = async (domainInput?: string) => {
    const raw = (domainInput ?? lookupDomain).trim();
    if (!raw) return;
    setLookupLoading(true);
    setLookupError(null);
    try {
      const data = await apiFetch<any>(`/api/internal/stores/health?domain=${encodeURIComponent(raw)}`);
      const store = (data?.store ?? data) as StoreHealth;
      const owners = (data?.owners ?? data?.storeOwners ?? []) as StoreOwner[];
      setResolvedStore(store);
      setResolvedOwners(Array.isArray(owners) ? owners : []);
      setPlanTier((store?.planTier as (typeof PLAN_OPTIONS)[number]) || 'free');
      setRotatedKey(null);
      setDeleteConfirm('');
      setSetActiveState(EMPTY_ACTION);
      setSetPlanState(EMPTY_ACTION);
      setRotateState(EMPTY_ACTION);
      setPreviewState(EMPTY_ACTION);
      setExtendTrialState(EMPTY_ACTION);
      setTriggerScrapeState(EMPTY_ACTION);
      setDeleteState(EMPTY_ACTION);
      setLookupDomain(store.externalDomain || raw);
    } catch (err) {
      setResolvedStore(null);
      setResolvedOwners([]);
      setLookupError(errStatus(err) === 404 ? 'Store not found' : errMessage(err));
    } finally {
      setLookupLoading(false);
    }
  };

  const openStoreDesk = (domain: string) => {
    setAdminArea('store-desk');
    setLookupDomain(domain);
    lookupStore(domain);
  };

  const onSetActive = async () => {
    if (!resolvedStore || !canSupport) return;
    if (!activeReason.trim()) {
      setSetActiveState({ loading: false, error: 'Reason is required', success: null });
      return;
    }
    setSetActiveState({ loading: true, error: null, success: null });
    try {
      await apiFetch('/api/internal/stores/support/set-active', {
        method: 'POST',
        body: JSON.stringify({
          domain: resolvedStore.externalDomain,
          isActive: !resolvedStore.isActive,
          reason: activeReason.trim(),
        }),
      });
      setSetActiveState({ loading: false, error: null, success: resolvedStore.isActive ? 'Store paused.' : 'Store reactivated.' });
      setActiveReason('');
      await lookupStore(resolvedStore.externalDomain);
      storesQuery.refetch();
    } catch (err) {
      setSetActiveState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onSetPlan = async () => {
    if (!resolvedStore || !canStaff) return;
    setSetPlanState({ loading: true, error: null, success: null });
    try {
      await apiFetch('/api/internal/stores/update-plan', {
        method: 'POST',
        body: JSON.stringify({ domain: resolvedStore.externalDomain, planTier }),
      });
      setSetPlanState({ loading: false, error: null, success: `Plan updated to ${planTier}.` });
      await lookupStore(resolvedStore.externalDomain);
      storesQuery.refetch();
    } catch (err) {
      setSetPlanState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onExtendTrial = async () => {
    if (!resolvedStore || !canStaff) return;
    if (!trialReason.trim()) {
      setExtendTrialState({ loading: false, error: 'Reason is required', success: null });
      return;
    }
    setExtendTrialState({ loading: true, error: null, success: null });
    try {
      const res = await apiFetch<any>('/api/internal/stores/extend-trial', {
        method: 'POST',
        body: JSON.stringify({
          domain: resolvedStore.externalDomain,
          days: trialDays,
          reason: trialReason.trim(),
        }),
      });
      const next = formatDate(res?.trialEndsAt ?? null);
      setExtendTrialState({ loading: false, error: null, success: `Trial extended. New end date: ${next}` });
      setTrialReason('');
      await lookupStore(resolvedStore.externalDomain);
      storesQuery.refetch();
    } catch (err) {
      setExtendTrialState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onTriggerScrape = async () => {
    if (!resolvedStore || !canSupport) return;
    if (!scrapeUrl.trim()) {
      setTriggerScrapeState({ loading: false, error: 'URL is required', success: null });
      return;
    }
    if (!scrapeReason.trim()) {
      setTriggerScrapeState({ loading: false, error: 'Reason is required', success: null });
      return;
    }
    setTriggerScrapeState({ loading: true, error: null, success: null });
    try {
      const res = await apiFetch<any>('/api/internal/stores/trigger-scrape', {
        method: 'POST',
        body: JSON.stringify({
          domain: resolvedStore.externalDomain,
          url: scrapeUrl.trim(),
          reason: scrapeReason.trim(),
        }),
      });
      setTriggerScrapeState({ loading: false, error: null, success: `Scrape triggered. Pages scraped: ${res?.count ?? 0}` });
      setScrapeUrl('');
      setScrapeReason('');
      await lookupStore(resolvedStore.externalDomain);
      storesQuery.refetch();
    } catch (err) {
      setTriggerScrapeState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onRotateKey = async () => {
    if (!resolvedStore || !canStaff) return;
    if (!rotateReason.trim()) {
      setRotateState({ loading: false, error: 'Reason is required', success: null });
      return;
    }
    setRotateState({ loading: true, error: null, success: null });
    try {
      const res = await apiFetch<any>('/api/internal/stores/rotate-api-key', {
        method: 'POST',
        body: JSON.stringify({ domain: resolvedStore.externalDomain, reason: rotateReason.trim() }),
      });
      const newKey = res?.apiKey || res?.key || '';
      setRotatedKey(newKey || null);
      setRotateReason('');
      setRotateState({ loading: false, error: null, success: 'API key rotated.' });
    } catch (err) {
      setRotateState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onPreview = async () => {
    if (!resolvedStore || !canSupport) return;
    setPreviewState({ loading: true, error: null, success: null });
    try {
      const res = await apiFetch<any>('/api/internal/preview/session', {
        method: 'POST',
        body: JSON.stringify({ targetStoreId: resolvedStore.id, reason: previewReason.trim() || 'Staff preview validation' }),
      });
      if (res?.previewUrl) {
        window.open(res.previewUrl, '_blank', 'noopener,noreferrer');
      }
      setPreviewState({ loading: false, error: null, success: 'Preview launched.' });
    } catch (err) {
      setPreviewState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onAddStore = async () => {
    if (!canStaff) return;
    if (!addDomain.trim()) {
      setAddState({ loading: false, error: 'Domain is required', success: null });
      return;
    }
    setAddState({ loading: true, error: null, success: null });
    try {
      const res = await apiFetch<any>('/api/internal/stores/create', {
        method: 'POST',
        body: JSON.stringify({
          domain: addDomain.trim().toLowerCase(),
          platform: addPlatform,
          planTier: addPlan,
          linkUserEmail: addEmail.trim() || undefined,
        }),
      });
      const msg = res?.created ? `Store created: ${res.domain}` : `Store already exists: ${res.domain}`;
      setAddState({ loading: false, error: null, success: msg });
      setAddDomain('');
      setAddEmail('');
      setAddPlan('free');
      storesQuery.refetch();
    } catch (err) {
      setAddState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const onDeleteStore = async () => {
    if (!resolvedStore || !canSuperadmin) return;
    if (deleteConfirm !== resolvedStore.externalDomain) {
      setDeleteState({ loading: false, error: `Type "${resolvedStore.externalDomain}" to confirm`, success: null });
      return;
    }
    setDeleteState({ loading: true, error: null, success: null });
    try {
      await apiFetch(`/api/internal/stores/${encodeURIComponent(resolvedStore.externalDomain)}`, { method: 'DELETE' });
      setDeleteState({ loading: false, error: null, success: 'Store deleted.' });
      setResolvedStore(null);
      setResolvedOwners([]);
      setDeleteConfirm('');
      setLookupDomain('');
      storesQuery.refetch();
    } catch (err) {
      setDeleteState({ loading: false, error: errMessage(err), success: null });
    }
  };

  const setUserAction = (email: string, next: ActionState) => {
    setUserActionState((prev) => ({ ...prev, [email]: next }));
  };

  const onSaveRole = async (row: UserRow) => {
    if (!canSuperadmin) return;
    const role = pendingRole[row.email] || row.role;
    setUserAction(row.email, { loading: true, error: null, success: null });
    try {
      await apiFetch('/api/internal/users/set-role', {
        method: 'POST',
        body: JSON.stringify({ email: row.email, role }),
      });
      setUserAction(row.email, { loading: false, error: null, success: 'Role updated.' });
      usersQuery.refetch();
    } catch (err) {
      setUserAction(row.email, { loading: false, error: errMessage(err), success: null });
    }
  };

  const onForceLogout = async (row: UserRow) => {
    if (!canSuperadmin) return;
    if (!window.confirm('This will invalidate all active sessions for this user.')) return;
    setUserAction(row.email, { loading: true, error: null, success: null });
    try {
      await apiFetch('/api/internal/users/invalidate-sessions', {
        method: 'POST',
        body: JSON.stringify({ email: row.email }),
      });
      setUserAction(row.email, { loading: false, error: null, success: 'Sessions invalidated.' });
    } catch (err) {
      setUserAction(row.email, { loading: false, error: errMessage(err), success: null });
    }
  };

  const ownerText =
    resolvedOwners.length > 0
      ? resolvedOwners.map((o) => `${o.email || 'unknown'} (${o.role})`).join(', ')
      : '—';

  const isWordPress = (resolvedStore?.platform || '').toLowerCase() === 'wordpress';
  const canTriggerScrapeForPlan = ['agent', 'rep', 'vea'].includes((resolvedStore?.planTier || '').toLowerCase());

  return (
    <div className="theme-page-bg space-y-6 p-6">
      <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-cyan-700">Operations workspace</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-950">Internal admin console</h1>
            <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-500">
              Fleet health, store interventions, revenue controls, and system access are split so staff can start from the right operational lane.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {ADMIN_AREAS.map((area) => (
              <button
                key={area.id}
                type="button"
                onClick={() => setAdminArea(area.id)}
                className={`rounded-2xl border p-3 text-left transition-all ${
                  adminArea === area.id
                    ? 'border-cyan-200 bg-cyan-50 text-cyan-950 shadow-sm'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-100 hover:bg-slate-50'
                }`}
              >
                <span className="text-sm font-black">{area.label}</span>
                <span className="mt-1 block text-[11px] font-medium leading-4 opacity-75">{area.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics */}
      {adminArea === 'fleet' && canSupport && !storesQuery.isLoading && storesQuery.data && (
        <div className="theme-section-card">
          <h2 className="mb-4 text-lg font-semibold text-zinc-900">Overview</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            <MetricCard label="Total Stores" value={metrics.total} />
            <MetricCard label="Active" value={metrics.active} sub={`${metrics.inactive} inactive`} />
            <MetricCard label="Paid" value={metrics.paid} sub={`${metrics.total - metrics.paid} free`} />
            <MetricCard label="Shopify" value={metrics.shopify} />
            <MetricCard label="WordPress" value={metrics.wordpress} />
            <MetricCard label="Standard" value={metrics.standard} />
            <MetricCard label="Users" value={usersQuery.data?.length ?? '—'} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {PLAN_OPTIONS.map((p) => (
              <span key={p} className="text-xs text-zinc-600">
                <span className="font-medium capitalize">{p}</span>: {metrics.byPlan[p] ?? 0}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Store Lookup */}
      {adminArea === 'store-desk' && (
      <div className="theme-section-card">
        <h2 className="mb-3 text-lg font-semibold text-zinc-900">Store Desk</h2>
        <p className="mb-4 text-sm text-zinc-500">Select one store, then work through Overview, Store Access, Plan & Billing, Security & Keys, and Lifecycle Actions.</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            value={lookupDomain}
            onChange={(e) => setLookupDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && lookupStore()}
            placeholder="Store domain — example.com or mystore.myshopify.com"
            className="theme-input"
          />
          <button
            onClick={() => lookupStore()}
            disabled={lookupLoading || !lookupDomain.trim()}
            className="theme-btn-primary"
          >
            {lookupLoading ? 'Looking up...' : 'Look up'}
          </button>
        </div>
        {lookupError && <p className="theme-msg-error mt-3 text-sm">{lookupError}</p>}
      </div>
      )}

      {/* Store Detail + Actions */}
      {adminArea === 'store-desk' && resolvedStore && (
        <>
          <div className="theme-section-card">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {platformBadge(resolvedStore.platform)}
                <h3 className="text-lg font-semibold text-zinc-900">Overview — {resolvedStore.externalDomain}</h3>
              </div>
              <button
                onClick={() => lookupStore(resolvedStore.externalDomain)}
                className="text-sm font-medium text-zinc-600 underline hover:text-zinc-900"
              >
                Refresh
              </button>
            </div>

            <div className="grid gap-x-6 gap-y-2 text-sm text-zinc-700 sm:grid-cols-2">
              <div className="flex items-center gap-2"><span className="font-medium text-zinc-900">Plan:</span> {planBadge(resolvedStore.planTier)}</div>
              <div className="flex items-center gap-2"><span className="font-medium text-zinc-900">Subscription:</span> {statusBadge(resolvedStore.subscriptionStatus)}</div>
              <div><span className="font-medium text-zinc-900">Billing:</span> {resolvedStore.billingProvider}</div>
              <div><span className="font-medium text-zinc-900">Active:</span> {resolvedStore.isActive ? 'Yes' : 'No'}</div>
              <div><span className="font-medium text-zinc-900">Trial ends:</span> {formatDate(resolvedStore.trialEndsAt)}</div>
              <div><span className="font-medium text-zinc-900">Grace ends:</span> {formatDate(resolvedStore.gracePeriodEndsAt)}</div>
              <div><span className="font-medium text-zinc-900">Plan until:</span> {formatDate(resolvedStore.planEffectiveUntil)}</div>
              <div><span className="font-medium text-zinc-900">AI this month:</span> {resolvedStore.aiInteractionsThisMonth ?? 0}</div>
              <div className="sm:col-span-2"><span className="font-medium text-zinc-900">Owners:</span> {ownerText}</div>
            </div>
          </div>

          <div className="theme-section-card">
            <h3 className="mb-4 text-lg font-semibold text-zinc-900">Quick Interventions</h3>
            <div className="space-y-6">
              {canSupport && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Lifecycle Actions — Set Active</p>
                  <p className="mt-1 text-sm text-zinc-600">Store is currently: <strong>{resolvedStore.isActive ? 'Active' : 'Paused'}</strong></p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      value={activeReason}
                      onChange={(e) => setActiveReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="theme-input"
                    />
                    <button
                      onClick={onSetActive}
                      disabled={setActiveState.loading}
                      className="theme-btn-primary"
                    >
                      {setActiveState.loading ? 'Saving...' : resolvedStore.isActive ? 'Pause Store' : 'Reactivate Store'}
                    </button>
                  </div>
                  {setActiveState.error && <p className="theme-msg-error mt-2 text-sm">{setActiveState.error}</p>}
                  {setActiveState.success && <p className="theme-msg-success mt-2 text-sm">{setActiveState.success}</p>}
                </div>
              )}

              {canStaff && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Plan & Billing — Override Plan</p>
                  <p className="mt-1 text-sm text-zinc-600">Current plan: <strong>{resolvedStore.planTier}</strong></p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <select
                      value={planTier}
                      onChange={(e) => setPlanTier(e.target.value as (typeof PLAN_OPTIONS)[number])}
                      className="theme-input"
                    >
                      {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <button
                      onClick={onSetPlan}
                      disabled={setPlanState.loading}
                      className="theme-btn-primary"
                    >
                      {setPlanState.loading ? 'Saving...' : 'Set Plan'}
                    </button>
                  </div>
                  {setPlanState.error && <p className="theme-msg-error mt-2 text-sm">{setPlanState.error}</p>}
                  {setPlanState.success && <p className="theme-msg-success mt-2 text-sm">{setPlanState.success}</p>}
                </div>
              )}

              {canStaff && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Plan & Billing — Trial Extension</p>
                  <p className="mt-1 text-sm text-zinc-600">Current trial end: <strong>{formatDate(resolvedStore.trialEndsAt)}</strong></p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={trialDays}
                      onChange={(e) => {
                        const n = Number(e.target.value || 0);
                        setTrialDays(Math.min(365, Math.max(1, Number.isFinite(n) ? n : 7)));
                      }}
                      className="theme-input"
                    />
                    <input
                      value={trialReason}
                      onChange={(e) => setTrialReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="theme-input sm:col-span-2"
                    />
                  </div>
                  <button
                    onClick={onExtendTrial}
                    disabled={extendTrialState.loading}
                    className="theme-btn-primary mt-3"
                  >
                    {extendTrialState.loading ? 'Extending...' : 'Extend Trial'}
                  </button>
                  {extendTrialState.error && <p className="theme-msg-error mt-2 text-sm">{extendTrialState.error}</p>}
                  {extendTrialState.success && <p className="theme-msg-success mt-2 text-sm">{extendTrialState.success}</p>}
                </div>
              )}

              {canSupport && canTriggerScrapeForPlan && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Lifecycle Actions — Trigger Scrape</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <input
                      value={scrapeUrl}
                      onChange={(e) => setScrapeUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="theme-input"
                    />
                    <input
                      value={scrapeReason}
                      onChange={(e) => setScrapeReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="theme-input"
                    />
                  </div>
                  <button
                    onClick={onTriggerScrape}
                    disabled={triggerScrapeState.loading}
                    className="theme-btn-primary mt-3"
                  >
                    {triggerScrapeState.loading ? 'Triggering...' : 'Trigger Scrape'}
                  </button>
                  {triggerScrapeState.error && <p className="theme-msg-error mt-2 text-sm">{triggerScrapeState.error}</p>}
                  {triggerScrapeState.success && <p className="theme-msg-success mt-2 text-sm">{triggerScrapeState.success}</p>}
                </div>
              )}

              {canStaff && isWordPress && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Security & Keys — Rotate API Key</p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      value={rotateReason}
                      onChange={(e) => setRotateReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="theme-input"
                    />
                    <button
                      onClick={onRotateKey}
                      disabled={rotateState.loading}
                      className="theme-btn-primary"
                    >
                      {rotateState.loading ? 'Rotating...' : 'Rotate Key'}
                    </button>
                  </div>
                  {rotateState.error && <p className="theme-msg-error mt-2 text-sm">{rotateState.error}</p>}
                  {rotateState.success && <p className="theme-msg-success mt-2 text-sm">{rotateState.success}</p>}
                  {rotatedKey && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="mb-2 text-sm text-amber-800">Share this key with the merchant — it cannot be retrieved again.</p>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <code className="block overflow-x-auto rounded bg-zinc-900 px-3 py-2 text-xs text-zinc-100">{rotatedKey}</code>
                        <button
                          className="theme-btn-outline"
                          onClick={() => navigator.clipboard.writeText(rotatedKey)}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {canSupport && (
                <div className="theme-subcard">
                  <p className="text-sm font-semibold text-zinc-900">Store Access — Staff Preview</p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      value={previewReason}
                      onChange={(e) => setPreviewReason(e.target.value)}
                      className="theme-input"
                    />
                    <button
                      onClick={onPreview}
                      disabled={previewState.loading}
                      className="theme-btn-primary"
                    >
                      {previewState.loading ? 'Launching...' : 'Launch Preview'}
                    </button>
                  </div>
                  {previewState.error && <p className="theme-msg-error mt-2 text-sm">{previewState.error}</p>}
                  {previewState.success && <p className="theme-msg-success mt-2 text-sm">{previewState.success}</p>}
                </div>
              )}

              {/* Danger Zone */}
              {canSuperadmin && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                  <p className="text-sm font-semibold text-red-700">Lifecycle Actions — Delete Store</p>
                  <p className="theme-msg-error mt-1 text-sm">
                    Permanently deletes <strong>{resolvedStore.externalDomain}</strong> and all linked user associations. This cannot be undone.
                  </p>
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                    <input
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder={`Type "${resolvedStore.externalDomain}" to confirm`}
                      className="w-full rounded-xl border border-red-300 bg-white px-3 py-2 text-sm"
                    />
                    <button
                      onClick={onDeleteStore}
                      disabled={deleteState.loading || deleteConfirm !== resolvedStore.externalDomain}
                      className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deleteState.loading ? 'Deleting...' : 'Delete Store'}
                    </button>
                  </div>
                  {deleteState.error && <p className="mt-2 text-sm text-red-700">{deleteState.error}</p>}
                  {deleteState.success && <p className="theme-msg-success mt-2 text-sm">{deleteState.success}</p>}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {adminArea === 'revenue' && canSupport && (
        <SectionCard title="Revenue" description="Plan mix, billing exceptions, and subscription operations live here. Promo code tooling stays in its dedicated workflow for now.">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MetricCard label="Paid Stores" value={metrics.paid} sub={`${metrics.total - metrics.paid} free`} />
            <MetricCard label="Past Due" value={(storesQuery.data || []).filter((s) => s.subscriptionStatus === 'past_due').length} />
            <MetricCard label="Cancelled" value={(storesQuery.data || []).filter((s) => s.subscriptionStatus === 'cancelled').length} />
            <MetricCard label="Grace Risk" value={(storesQuery.data || []).filter((s) => s.subscriptionStatus === 'past_due').length} sub="needs review" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {PLAN_OPTIONS.map((p) => (
              <span key={p} className="rounded-full bg-slate-50 px-3 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200">
                {p}: {metrics.byPlan[p] ?? 0}
              </span>
            ))}
          </div>
          <Link to="/promo-codes" className="mt-4 inline-flex rounded-xl border border-cyan-100 bg-cyan-50 px-4 py-2 text-sm font-bold text-cyan-800 hover:bg-cyan-100">
            Open promo codes
          </Link>
        </SectionCard>
      )}

      {/* Add Store */}
      {adminArea === 'revenue' && canStaff && (
        <div className="theme-section-card">
          <h3 className="mb-4 text-lg font-semibold text-zinc-900">Add Store</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              value={addDomain}
              onChange={(e) => setAddDomain(e.target.value)}
              placeholder="Domain — example.com"
              className="theme-input"
            />
            <select
              value={addPlatform}
              onChange={(e) => setAddPlatform(e.target.value as 'shopify' | 'wordpress' | 'standard')}
              className="theme-input"
            >
              <option value="standard">Standard site</option>
              <option value="shopify">Shopify</option>
              <option value="wordpress">WordPress</option>
            </select>
            <input
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              placeholder="Owner email (optional)"
              className="theme-input"
            />
            <select
              value={addPlan}
              onChange={(e) => setAddPlan(e.target.value as (typeof PLAN_OPTIONS)[number])}
              className="theme-input"
            >
              {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <button
            onClick={onAddStore}
            disabled={addState.loading || !addDomain.trim()}
            className="theme-btn-primary mt-3"
          >
            {addState.loading ? 'Creating...' : 'Create Store'}
          </button>
          {addState.error && <p className="theme-msg-error mt-2 text-sm">{addState.error}</p>}
          {addState.success && <p className="theme-msg-success mt-2 text-sm">{addState.success}</p>}
        </div>
      )}

      {/* Store List */}
      {adminArea === 'fleet' && canSupport && (
        <div className="theme-section-card">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-zinc-900">Store List</h3>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search domain..."
              className="theme-input w-full max-w-xs"
            />
          </div>

          {storesQuery.isLoading && <p className="text-sm text-zinc-500">Loading stores...</p>}
          {storesQuery.isError && <p className="theme-msg-error text-sm">Failed to load stores.</p>}
          {!storesQuery.isLoading && !storesQuery.isError && filteredStores.length === 0 && (
            <p className="text-sm text-zinc-500">{search ? 'No stores match your search.' : 'No stores found.'}</p>
          )}

          {filteredStores.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-zinc-500">
                  <tr className="border-b border-zinc-200">
                    <th className="px-3 py-2 font-medium">Domain</th>
                    <th className="px-3 py-2 font-medium">Platform</th>
                    <th className="px-3 py-2 font-medium">Plan</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Active</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStores.map((s) => (
                    <tr key={s.id || s.externalDomain} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2">
                        <button
                          className="font-medium text-zinc-900 underline"
                          onClick={() => openStoreDesk(s.externalDomain)}
                        >
                          {s.externalDomain}
                        </button>
                      </td>
                      <td className="px-3 py-2">{platformBadge(s.platform)}</td>
                      <td className="px-3 py-2">{planBadge(s.planTier)}</td>
                      <td className="px-3 py-2">{statusBadge(s.subscriptionStatus)}</td>
                      <td className="px-3 py-2">{s.isActive ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2">{formatDate(s.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* User Management */}
      {adminArea === 'system' && (
      <>
      <SectionCard title="System" description="User access, roles, session invalidation, and lifecycle audit trails.">
        <Link to="/action-log" className="inline-flex rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
          Open action log
        </Link>
      </SectionCard>
      <div className="theme-section-card">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-zinc-900">Launch Feedback</h3>
            <p className="mt-1 text-sm text-zinc-500">
              Anonymous widget-close and exit-intent feedback for this console environment. Prod console will show `veahub.com` traffic and staging console will show demo traffic when that widget is pointed at staging.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={feedbackSiteContext}
              onChange={(e) => setFeedbackSiteContext(e.target.value as typeof feedbackSiteContext)}
              className="rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            >
              <option value="all">All sites</option>
              <option value="marketing_site">Marketing site</option>
              <option value="demo_site">Demo site</option>
            </select>
            <input
              value={feedbackSearch}
              onChange={(e) => setFeedbackSearch(e.target.value)}
              placeholder="Search domain, issue, or type"
              className="rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {launchFeedbackQuery.isLoading && <p className="text-sm text-zinc-500">Loading launch feedback...</p>}
        {launchFeedbackQuery.isError && <p className="theme-msg-error text-sm">Failed to load launch feedback.</p>}
        {!launchFeedbackQuery.isLoading && !launchFeedbackQuery.isError && (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                Total: {launchFeedbackQuery.data?.total ?? 0}
              </span>
              {(launchFeedbackQuery.data?.summary || []).map((row) => (
                <span key={`${row.siteContext}-${row.trigger}`} className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-900">
                  {row.siteContext.replace('_', ' ')} · {row.trigger.replace('_', ' ')}: {row.count}
                </span>
              ))}
            </div>

            {(launchFeedbackQuery.data?.rows || []).length === 0 ? (
              <p className="text-sm text-zinc-500">No feedback yet for this filter.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-zinc-500">
                    <tr className="border-b border-zinc-200">
                      <th className="px-3 py-2 font-medium">When</th>
                      <th className="px-3 py-2 font-medium">Site</th>
                      <th className="px-3 py-2 font-medium">Domain</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Severity</th>
                      <th className="px-3 py-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(launchFeedbackQuery.data?.rows || []).map((row) => (
                      <tr key={row.id} className="border-b border-zinc-100 align-top">
                        <td className="px-3 py-2 whitespace-nowrap text-zinc-600">{formatDate(row.createdAt)}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-zinc-900">{row.siteContext.replace('_', ' ')}</span>
                            <span className="text-xs text-zinc-500">{row.trigger.replace('_', ' ')}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <button
                              type="button"
                              onClick={() => openStoreDesk(row.storeDomain)}
                              className="text-left font-medium text-cyan-700 underline hover:text-cyan-900"
                            >
                              {row.storeDomain}
                            </button>
                            {row.page && <span className="max-w-[24rem] truncate text-xs text-zinc-500">{row.page}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-zinc-900">{row.feedbackType.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-zinc-500">
                              widget: {row.hadWidgetOpen ? 'yes' : 'no'} · chat: {row.hadChatInteraction ? 'yes' : 'no'} · messages: {row.messageCount ?? 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-zinc-700">{row.severity || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="max-w-[34rem] space-y-1">
                            {row.goal && <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Goal: {row.goal}</p>}
                            <p className="text-sm leading-6 text-zinc-800">{row.details}</p>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
      <div className="theme-section-card">
        <h3 className="mb-4 text-lg font-semibold text-zinc-900">User Management</h3>
        {usersQuery.isLoading && <p className="text-sm text-zinc-500">Loading users...</p>}
        {usersQuery.isError && <p className="theme-msg-error text-sm">Failed to load users.</p>}
        {!usersQuery.isLoading && !usersQuery.isError && (usersQuery.data || []).length === 0 && (
          <p className="text-sm text-zinc-500">No users found.</p>
        )}
        {(usersQuery.data || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(usersQuery.data || []).map((u) => {
                  const action = userActionState[u.email] || EMPTY_ACTION;
                  return (
                    <tr key={u.email} className="border-b border-zinc-100 align-top">
                      <td className="px-3 py-2 text-zinc-900">{u.email}</td>
                      <td className="px-3 py-2">{u.role}</td>
                      <td className="px-3 py-2">{formatDate(u.createdAt || null)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          {canSuperadmin && (
                            <>
                              <select
                                value={pendingRole[u.email] || u.role}
                                onChange={(e) => setPendingRole((prev) => ({ ...prev, [u.email]: e.target.value as UserRow['role'] }))}
                                className="rounded-lg border border-zinc-300 px-2 py-1 text-xs"
                              >
                                <option value="user">user</option>
                                <option value="support">support</option>
                                <option value="staff">staff</option>
                                <option value="superadmin">superadmin</option>
                              </select>
                              <button
                                onClick={() => onSaveRole(u)}
                                disabled={action.loading}
                                className="theme-btn-outline"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => onForceLogout(u)}
                                disabled={action.loading}
                                className="rounded-lg border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                              >
                                Force Logout
                              </button>
                            </>
                          )}
                          {!canSuperadmin && <span className="text-xs text-zinc-500">No actions for your role.</span>}
                        </div>
                        {action.error && <p className="theme-msg-error mt-2 text-xs">{action.error}</p>}
                        {action.success && <p className="theme-msg-success mt-2 text-xs">{action.success}</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default InternalAdmin;
